import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { BaseResource } from '../../core/abstractions/base.resource';
import { AgendaItemImportedVoteResponseModel } from '../../models/agenda-item-imported-vote-model.model';
import { AgendaItemSettingsModel } from '../../models/agenda-item-settings.model';
import { AgendaItemModel } from '../../models/agenda-item.model';
import { ChatMessageModel } from '../../models/chat-message.model';
import { ActionResultModel } from '../../models/core/action-result.model';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { PostValueResponseModel } from '../../models/core/post-value-response.model';
import { ResourceConfig } from '../../models/core/resource.config';
import { CurrentShareholderGeneralMeetingModel } from '../../models/current-shareholder-general-meeting.model';
import { DmiSettingsModel } from '../../models/dmi-settings.model';
import { AgendaVoteOptionEnum } from '../../models/enums/agenda-vote-option.enum';
import { ResponseTypeEnum } from '../../models/enums/response-type.enum';
import { VoteOptionEnum } from '../../models/enums/vote-option.enum';
import { GeneralMeetingModel } from '../../models/general-meeting.model';
import { PresenceModel } from '../../models/presence.model';
import { ShareholderInstructionsSummaryModel } from '../../models/shareholder-instructions-summary.model';
import { DictionaryType } from '../../models/types/dictionary.type';
import { VotingMonitorItemModel } from '../../models/voting-monitor-item.model';
import { ZoomSignatureRequestModel } from '../../models/zoom-signature-request.model';
import { ZoomSignatureResponseModel } from '../../models/zoom-signature-response.model';

@Injectable({
    providedIn: 'root'
})
export class GeneralMeetingsResource extends BaseResource {
    constructor() {
        super(new ResourceConfig({ baseController: 'general-meetings' }));
    }

    /**
     * Returns a summary of the general meeting.
     */
    get(id: number): Observable<ApiResponseModel<GeneralMeetingModel>> {
        return this._requestService.makeGet<ApiResponseModel<GeneralMeetingModel>>(this.getUrl(`${id}/summary`));
    }

    /**
     * Returns a summary of the current general meeting.
     */
    getCurrent(): Observable<ApiResponseModel<GeneralMeetingModel>> {
        return this._requestService.makeGet<ApiResponseModel<GeneralMeetingModel>>(this.getUrl('current/summary'));
    }

    update(id: number, model: GeneralMeetingModel): Observable<ApiResponseModel<ActionResultModel>> {
        return this._requestService.makePut<ApiResponseModel<ActionResultModel>>(this.getUrl(`${id}`), model);
    }

    /**
     * Returns the presence of the current general meeting.
     */
    getCurrentPresence(): Observable<ApiResponseModel<PresenceModel>> {
        return this._requestService.makeGet<ApiResponseModel<PresenceModel>>(this.getUrl('current/presence'));
    }

    /**
     * Upload powerPoint file for presence presenter
     */
    uploadGmPresencePowerPoint(fileData: string): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl('current/upload-presence-power-point'), fileData);
    }

    /**
     * Show presence info in gm presenter
     */
    showPresenceInPresenter(): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl('current/presenter-show-presence'));
    }

    /**
     * Returns the agenda items of the current general meeting.
     */
    getCurrentAgendaItems(): Observable<ApiResponseModel<AgendaItemModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<AgendaItemModel[]>>(this.getUrl('current/agenda-items'));
    }

    /**
     * Returns the agenda item's settings of the current general meeting.
     */
    getCurrentAgendaItemSettings(agendaItemNumber: number): Observable<ApiResponseModel<AgendaItemSettingsModel>> {
        return this._requestService.makeGet<ApiResponseModel<AgendaItemSettingsModel>>(
            this.getUrl(`current/agenda-item/${agendaItemNumber}`)
        );
    }

    /**
     * Updates the agenda item's settings of the current general meeting.
     */
    updateCurrentAgendaItemSettings(model: AgendaItemSettingsModel): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePut<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/agenda-item/${model.gmAgendaItemNumber}`),
            model
        );
    }

    /**
     * Duplicate an agenda item.
     */
    duplicateAgendaItem(agendaItemNumber: number): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/agenda-item/${agendaItemNumber}/duplicate`)
        );
    }

    /**
     * Delete an agenda item.
     */
    deleteAgendaItem(agendaItemNumber: number): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/agenda-item/${agendaItemNumber}/delete`)
        );
    }

    /**
     * Returns the agenda items of the current shareholder and general meeting.
     */
    getCurrentAgendaItemsForShareholder(): Observable<ApiResponseModel<AgendaItemModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<AgendaItemModel[]>>(this.getUrl('current/shareholders/current/agenda-items'));
    }

    /**
     * Returns a summary of the current registration instructions.
     */
    getCurrentInstructionsForShareholder(): Observable<ApiResponseModel<ShareholderInstructionsSummaryModel>> {
        return this._requestService.makeGet<ApiResponseModel<ShareholderInstructionsSummaryModel>>(
            this.getUrl('current/shareholders/current/instructions')
        );
    }

    /**
     * Start an agenda voting (optional a default options nr and custom coupon nr)
     */
    startVotingOnAgendaItem(
        itemNumber: number,
        votingDefaultOptionsNumber?: VoteOptionEnum,
        customCouponNumber?: number
    ): Observable<ApiResponseModel<PostValueResponseModel>> {
        let url = `current/agenda-items/${itemNumber}/start-voting`;
        const params = new URLSearchParams();
        if (votingDefaultOptionsNumber !== undefined) {
            params.append('votingDefaultOptionsNumber', votingDefaultOptionsNumber.toString());
        }
        if (customCouponNumber !== undefined) {
            params.append('customCouponNumber', customCouponNumber.toString());
        }
        if (params.size > 0) {
            url += `?${params.toString()}`;
        }
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(this.getUrl(url));
    }
    /**
     * Close an agenda voting
     */
    closeVotingOnAgendaItem(itemNumber: number): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/agenda-items/${itemNumber}/close-voting`)
        );
    }
    /**
     * Delete an agenda voting.
     */
    deleteVotingOnAgendaItem(itemNumber: number): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/agenda-items/${itemNumber}/delete-voting`)
        );
    }

    /**
     * Vote on an agenda item.
     */
    voteOnAgendaItem(itemNumber: number, option: VoteOptionEnum): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/shareholders/current/agenda-items/${itemNumber}/vote/${option}`)
        );
    }

    /**
     * Returns a summary of the current general meeting for the current shareholder.
     */
    getCurrentForShareholder(): Observable<ApiResponseModel<CurrentShareholderGeneralMeetingModel>> {
        return this._requestService.makeGet<ApiResponseModel<CurrentShareholderGeneralMeetingModel>>(
            this.getUrl('current/shareholders/current/summary')
        );
    }

    /**
     * Returns true if the current shareholder is checked in the current general meeting.
     */
    getCurrentCheckedInForShareholder(): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makeGet<ApiResponseModel<boolean>>(this.getUrl('current/shareholders/current/checked-in'));
    }

    /**
     * Registers the current shareholder for the meeting.
     */
    selfRegister(): Observable<ApiResponseModel<ActionResultModel>> {
        return this._requestService.makePost<ApiResponseModel<ActionResultModel>>(
            this.getUrl('current/shareholders/current/register-self')
        );
    }

    /**
     * Saves the current shareholder's instructions for the meeting.
     */
    registerInstructions(
        votes: DictionaryType<VoteOptionEnum>,
        instructionAgendaItemChange?: AgendaVoteOptionEnum,
        instructionNewAgendaItem?: AgendaVoteOptionEnum
    ): Observable<ApiResponseModel<ActionResultModel>> {
        let queryParams = `instructionAgendaItemChange=${!!instructionAgendaItemChange ? instructionAgendaItemChange : 1}`;
        queryParams += `&instructionNewAgendaItem=${!!instructionNewAgendaItem ? instructionNewAgendaItem : 1}`;

        return this._requestService.makePost<ApiResponseModel<ActionResultModel>>(
            this.getUrl(`current/shareholders/current/register-independent?${queryParams}`),
            votes
        );
    }

    /**
     * Unregisters the current shareholder from the meeting.
     */
    unregister(): Observable<ApiResponseModel<ActionResultModel>> {
        return this._requestService.makePost<ApiResponseModel<ActionResultModel>>(this.getUrl('current/shareholders/current/unregister'));
    }

    /**
     * Returns the chat of the current general meeting.
     */
    exportChat(): Observable<any> {
        return this._requestService.makeGet<any>(
            this.getUrl('current/chat-messages/csv'),
            undefined,
            undefined,
            undefined,
            true,
            ResponseTypeEnum.ArrayBuffer
        );
    }

    /**
     * Deletes all chat messages for the current meeting
     */
    deleteChat(): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makeDelete<ApiResponseModel<boolean>>(this.getUrl('current/chat-messages'), undefined);
    }

    /**
     * Returns the chat of the current general meeting.
     */
    getCurrentChat(): Observable<ApiResponseModel<ChatMessageModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<ChatMessageModel[]>>(this.getUrl('current/chat-messages'));
    }

    /**
     * Returns the chat with the specified shareholder.
     */
    getChatWith(shareholderId: string): Observable<ApiResponseModel<ChatMessageModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<ChatMessageModel[]>>(this.getUrl(`current/chat-messages/${shareholderId}`));
    }

    /**
     * Sends a chat message.
     */
    sendChatMessage(message: string, toShareholderId?: any): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(this.getUrl('current/chat-messages'), {
            message,
            toShareholderId
        });
    }

    /**
     * Checks in a shareholder from the meeting
     */
    checkIn(): Observable<ApiResponseModel<ActionResultModel>> {
        return this._requestService.makePost<ApiResponseModel<ActionResultModel>>(this.getUrl('current/shareholders/current/check-in'));
    }

    /**
     * Checks out a shareholder from the meeting
     */
    checkOut(): Observable<ApiResponseModel<ActionResultModel>> {
        return this._requestService.makePost<ApiResponseModel<ActionResultModel>>(this.getUrl('current/shareholders/current/check-out'));
    }

    /**
     * Resends the email with the admission card to the logged in shareholder
     */
    resendGmAdmissionCard(): Observable<ApiResponseModel<PostValueResponseModel<boolean>>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel<boolean>>>(
            this.getUrl('current/shareholders/current/send-admission-card-email')
        );
    }

    /**
     * Accepts the voting on an agenda item.
     */
    acceptVoting(agendaItemNumber: number): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/agenda-items/${agendaItemNumber}/accept-voting`)
        );
    }

    /**
     * Revokes the acceptance of the voting on an agenda item.
     */
    undoAcceptVoting(agendaItemNumber: number): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/agenda-items/${agendaItemNumber}/undo-accept-voting`)
        );
    }

    /**
     * Monitors the current state of the voting.
     */
    votingMonitor(agendaItemNumber: number): Observable<ApiResponseModel<VotingMonitorItemModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<VotingMonitorItemModel[]>>(
            this.getUrl(`current/agenda-items/${agendaItemNumber}/vote-monitor`)
        );
    }

    submitQuestion(text: string, name: string, phone: string, email: string): Observable<PostValueResponseModel> {
        return this._requestService.makePost<PostValueResponseModel>(this.getUrl('current/question'), { text, name, phone, email });
    }

    /**
     * Exports a general meeting
     */
    exportGeneralMeeting(id: number): Observable<ApiResponseModel<string>> {
        return this._requestService.makePost<ApiResponseModel<string>>(this.getUrl(`${id}/export`));
    }

    /**
     * Imports a general meeting backup file as base64 string
     */
    importGeneralMeeting(data: string): Observable<ApiResponseModel<ActionResultModel>> {
        return this._requestService.makePost<ApiResponseModel<ActionResultModel>>(this.getUrl('import'), data);
    }

    /**
     * Imports multiple votes for an agenda item from barcodes
     */
    importVotes(
        agendaItemNumber: number,
        barcodes: string[],
        voteOption: VoteOptionEnum
    ): Observable<ApiResponseModel<PostValueResponseModel<AgendaItemImportedVoteResponseModel[]>>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel<AgendaItemImportedVoteResponseModel[]>>>(
            this.getUrl(`current/agenda-items/${agendaItemNumber}/import-votes/${voteOption}`),
            barcodes
        );
    }

    /**
     * Resets the vote of a shareholder for an agenda item
     */
    resetVote(agendaItemNumber: number, shareholderId: number): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`current/agenda-items/${agendaItemNumber}/votes/reset/${shareholderId}`)
        );
    }

    /**
     * Request a signature to access the zoom meeting.
     */
    requestZoomSignature(meetingNumber: number): Observable<ApiResponseModel<ZoomSignatureResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<ZoomSignatureResponseModel>>(
            this.getUrl('request-zoom-signature'),
            new ZoomSignatureRequestModel({ meetingNumber })
        );
    }

    /**
     * Returns the DMI settings of the current general meeting.
     */
    getCurrentDmiSettings(): Observable<ApiResponseModel<DmiSettingsModel>> {
        return this._requestService.makeGet<ApiResponseModel<DmiSettingsModel>>(this.getUrl('current/dmi_settings'));
    }

    /**
     * Saves the DMI settings of the current general meeting.
     */
    saveCurrentDmiSettings(settings: DmiSettingsModel): Observable<ApiResponseModel<DmiSettingsModel>> {
        return this._requestService.makePut<ApiResponseModel<DmiSettingsModel>>(this.getUrl('current/dmi_settings'), settings);
    }

    /**
     * Initializes the DMI server.
     */
    initDmiServer(): Observable<ApiResponseModel<string>> {
        return this._requestService.makePut<ApiResponseModel<string>>(this.getUrl('current/dmi_init'));
    }

    /**
     * Resests the DMI server.
     */
    resetDmiServer(): Observable<ApiResponseModel<string>> {
        return this._requestService.makePut<ApiResponseModel<string>>(this.getUrl('current/dmi_reset'));
    }
}
